var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import styles from "./LoginPage.scss";
import { EXTERNAL_LINKS, MAX_EMAIL_LENGTH } from "@/entry/Authentication/constants";
import { isValidEmail } from "@/entry/Authentication/utils";
import { redirectToLoginWithEmailHash } from "@/entry/Authentication/webex-redirect";
import { loginI18n, t } from "./localization/login-i18n";
import { TIME_TO_LOAD_LOGIN_UI } from "@/entry/constants";
import { storeTiming } from "@/performance/time-to-interactive";
import { createLogger } from "@/sdk";
import teamsLogo from "@img/teams-icon-color.svg";
import { StructuredLog } from "@agentx/agentx-services";
import { html, LitElement } from "lit";
import { live } from "lit/directives/live.js";
import { fetchCatalogAndRedirectToWebexLogin, loginWithWebex } from "@/entry/utils/EntryUtils";
import "@momentum-ui/web-components";
import { customElement, property, state } from "lit/decorators.js";
export const logger = createLogger("[Login]");
/**
 * LoginPage component handles the authentication flow for Webex
 *
 * Loading States:
 * - isWebexInitializing: Shows loading spinner while Webex SDK initializes
 * - isEmailLoading: Shows loading state during email form submission
 */
let LoginPage = class LoginPage extends LitElement {
    constructor() {
        super(...arguments);
        // Email flow properties
        this.email = "";
        this.emailErrorMessage = "";
        this.isEmailLoading = false;
        this.isWebexInitializing = true;
        this.useWebExAuth = false;
        // Remove the following once we switch over to <agentx-entry> for login flow
        this.webexState = new URLSearchParams(window.location.search).get("state");
        this.handleI18nUpdate = () => {
            this.requestUpdate();
        };
        this.onLoginError = (emailErrorMessage) => {
            this.emailErrorMessage = emailErrorMessage;
            this.isEmailLoading = false;
            this.isWebexInitializing = false;
        };
        // Email flow handlers
        this.handleEmailChange = (e) => {
            this.email = e.detail.value;
            this.emailErrorMessage = "";
        };
        this.handleEmailSubmit = async (e) => {
            e.preventDefault();
            StructuredLog.trace("Login/LoginPage/EmailSubmit");
            // Validate email
            if (!isValidEmail(this.email)) {
                this.emailErrorMessage = "invalidEmail";
                return;
            }
            // Set loading state
            this.isEmailLoading = true;
            this.emailErrorMessage = "";
            if (this.useWebExAuth) {
                try {
                    await loginWithWebex(this.email, this.themeManager?.isDarkMode() ? "dark" : "light", this.onLoginError);
                }
                catch (error) {
                    StructuredLog.error("Login/LoginPage/WebexLogin/Error", "Failed to initiate Webex login", { error });
                    this.onLoginError("loginFailed");
                }
                return;
            }
            await redirectToLoginWithEmailHash(this.email);
        };
        this.handleEmailKeyPress = async (e) => {
            if (e.key === "Enter") {
                await this.handleEmailSubmit(e);
            }
        };
    }
    static get styles() {
        return styles;
    }
    async connectedCallback() {
        super.connectedCallback();
        loginI18n.on("initialized", this.handleI18nUpdate);
        loginI18n.on("languageChanged", this.handleI18nUpdate);
        if (loginI18n.isInitialized) {
            this.handleI18nUpdate();
        }
        await this.connectedCallbackCompletion();
    }
    /**
     * Preserves CRM-specific query parameters (sfdcIframeOrigin, orgId, canary)
     * into localStorage/sessionStorage before the OAuth login flow discards them.
     * CRM applications inject these params when embedding the desktop, and they are
     * required for CRM Connector V1 initialization after authentication completes.
     */
    preserveCrmQueryParams() {
        const urlParams = new URLSearchParams(window.location.search);
        const sfdcIframeOrigin = urlParams.get("sfdcIframeOrigin");
        if (sfdcIframeOrigin) {
            localStorage.setItem("sfdcIframeOrigin", sfdcIframeOrigin);
        }
        const orgId = urlParams.get("orgId");
        if (orgId) {
            sessionStorage.setItem("queryOrgId", orgId);
        }
        const isCanary = urlParams.get("canary");
        if (isCanary) {
            sessionStorage.setItem("canary", isCanary);
        }
    }
    async connectedCallbackCompletion() {
        StructuredLog.trace("Login/LoginPage/ConnectedCallback");
        if (!this.useWebExAuth && this.webexState && !this.emailErrorMessage) {
            await fetchCatalogAndRedirectToWebexLogin(this.webexState, this.onLoginError, false);
            return;
        }
        this.preserveCrmQueryParams();
        this.isWebexInitializing = false;
        this.storeLoginUITiming();
    }
    storeLoginUITiming() {
        storeTiming(TIME_TO_LOAD_LOGIN_UI, { duration: performance.now(), interactiveStep: false });
    }
    isSubmitDisabled() {
        return !isValidEmail(this.email) || this.isEmailLoading;
    }
    disconnectedCallback() {
        loginI18n.off("initialized", this.handleI18nUpdate);
        loginI18n.off("languageChanged", this.handleI18nUpdate);
        super.disconnectedCallback();
    }
    render() {
        return html `
      <md-theme ?darkTheme=${this.themeManager?.isDarkMode()}>
        <style>
          :host {
            --login-bg-url: url("${this.themeManager?.getThemeBg()}");
          }
        </style>
        <div class="login-container">
          <!-- Webex Logo -->
          <div class="logo">
            <img src="${teamsLogo}" alt="${t("logoAlt")}" />
          </div>

          <!-- Welcome Text -->
          <md-label class="title"><span class="title-text">${t("title")}</span></md-label>

          ${this.renderMainContent()}

          <!-- Help Link -->
          <a href=${EXTERNAL_LINKS.NEED_HELP_SIGNING_IN} target="_blank" rel="noopener noreferrer" class="help-link"
            >${t("helpLink")}</a
          >
        </div>

        <!-- Footer -->
        <footer class="footer">
          <p>
            <img src="${this.themeManager?.getWebexFooter()}" alt="${t("footerLogoAlt")}" class="footer-logo" /> ${t("footerText")}
            <a href=${EXTERNAL_LINKS.TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer"
              >${t("termsOfService")}</a
            >,
            <a href=${EXTERNAL_LINKS.PRIVACY_STATEMENT} target="_blank" rel="noopener noreferrer"
              >${t("privacyStatement")}</a
            >, ${t("noticesAndDisclaimers")}
            <a href=${EXTERNAL_LINKS.ABOUT_WEBEX} target="_blank" rel="noopener noreferrer">${t("aboutWebex")}</a>.
          </p>
        </footer>
      </md-theme>
    `;
    }
    renderMainContent() {
        if (this.isWebexInitializing) {
            return this.renderLoading();
        }
        return this.renderEmailFlow();
    }
    renderLoading() {
        return html `
      <div class="webex-loading">
        <span class="loading"></span>
      </div>
    `;
    }
    renderEmailFlow() {
        return html `
      <md-form @submit=${this.handleEmailSubmit}>
        <div class="form-group">
          <md-input
            type="email"
            id="IDToken1"
            class="form-input"
            label="${t("emailLabel")}"
            .value=${live(this.email)}
            @input-change=${this.handleEmailChange}
            @keypress=${this.handleEmailKeyPress}
            placeholder="${t("emailPlaceholder")}"
            ?disabled=${this.isEmailLoading}
            autocomplete="email"
            maxlength=${MAX_EMAIL_LENGTH}
            autofocus
          >
          </md-input>
          ${this.emailErrorMessage ? html `<div class="error-message">${t(this.emailErrorMessage)}</div>` : ""}
          <md-button
            type="submit"
            variant="primary"
            id="IDButton2"
            name="btnOk"
            class="login-button"
            ?disabled=${this.isSubmitDisabled()}
            @button-click=${this.handleEmailSubmit}
          >
            <span class="button-text">
              ${this.isEmailLoading ? html `<span class="loading"></span>` : t("signIn")}
            </span>
          </md-button>
        </div>
        <div class="horizontal-divider"></div>
      </md-form>
    `;
    }
};
__decorate([
    state()
], LoginPage.prototype, "email", void 0);
__decorate([
    property({ type: String })
], LoginPage.prototype, "emailErrorMessage", void 0);
__decorate([
    property({ type: Boolean })
], LoginPage.prototype, "isEmailLoading", void 0);
__decorate([
    property({ type: Boolean })
], LoginPage.prototype, "isWebexInitializing", void 0);
__decorate([
    property({ type: Object })
], LoginPage.prototype, "themeManager", void 0);
__decorate([
    property({ type: Boolean })
], LoginPage.prototype, "useWebExAuth", void 0);
LoginPage = __decorate([
    customElement("login-page")
], LoginPage);
export { LoginPage };
